import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { filter, map, tap } from 'rxjs/operators';
import { from } from 'rxjs';

import { ApiPartnerService } from '../services';

import { Guid } from '@fitscovery/common/types';
import { GetPartnerBySubdomain, GetPartnerById, CreatePartnerWebsite, UpdateLogoImage, UpdateImageDescriptions, UpdateAppConfig, UpdateTextSections, UpdateSocialMedia, UpdateContactInfo, UpdatePartnerSettings } from '../actions';
import { Partner, PartnerStateModel, PartnerResponse, PartnerSettingsResponse, PartnerSettingsDataResponse, PartnerAppConfigResponse, PartnerContactInfoResponse, PartnerContactInfoDataResponse } from '../models';
import { PartnerControlState } from './partner-control-state';
import { SnackbarService } from '@fitscovery/ui/snackbar';

export const partnerStateDefaults: PartnerStateModel = {
  data: null,
  loaded: false
};

@State<PartnerStateModel>({
  name: 'partner',
  defaults: partnerStateDefaults
})

@Injectable()
export class PartnerState extends PartnerControlState implements NgxsOnInit {

  @Selector()
  static partnerId(state: PartnerStateModel): string | Guid {
    return state.data!.id ?? Guid.empty;
  }

  @Selector()
  static partner(state: PartnerStateModel): Partner {
    return state.data!;
  }

  @Selector()
  static subdomain(state: PartnerStateModel): string {
    return state.data!.subdomain!;
  }

  @Selector()
  static loaded(state: PartnerStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private partnerService: ApiPartnerService,
    public override snackbar: SnackbarService,
  ) {
    super(snackbar);
  }

  ngxsOnInit(): void {
  }

  @Action(GetPartnerBySubdomain)
  getPartnerBySubdomain(ctx: StateContext<PartnerStateModel>, action: GetPartnerBySubdomain) {
    return from(this.partnerService.getPartnerBySubdomain(action.subdomain)).pipe(
      tap((data: Partner) => ctx.patchState({ data, loaded: true })),
      this.catchResponse,
    );
  }

  @Action(GetPartnerById)
  getPartnerById(ctx: StateContext<PartnerStateModel>, action: GetPartnerById): void {
    this.partnerService.getPartnerById(action.id).pipe(
      filter((response: PartnerResponse) => !!response.data || (response.data as any).length > 0),
      map((response: PartnerResponse) => response.data[0])
    ).subscribe((data: Partner) => ctx.patchState({ data, loaded: true }));
  }

  @Action(CreatePartnerWebsite)
  createPartnerWebsite(ctx: StateContext<PartnerStateModel>, action: CreatePartnerWebsite) {
    return this.partnerService.createPartnerWebsite(action.partnerData).pipe(
      this.catchResponse
    );
  }

  @Action(UpdateLogoImage)
  updateLogoImage(ctx: StateContext<PartnerStateModel>, action: UpdateLogoImage): void {
    this.partnerService.updateLogoImage(action.partnerId, action.logoUrl).subscribe(() => {
      ctx.patchState({
        data: { logoUrl: action.logoUrl, ...ctx.getState().data },
        loaded: true
      });
    });
  }

  @Action(UpdateImageDescriptions)
  updateImageDescriptions(ctx: StateContext<PartnerStateModel>, action: UpdateImageDescriptions): void {
    this.partnerService.updateImageDescriptions(action.partnerId, action.media).subscribe(() => {
      ctx.patchState({
        data: { imageDescriptions: action.media, ...ctx.getState().data },
        loaded: true
      });
    });
  }

  @Action(UpdateAppConfig)
  updateAppConfig(ctx: StateContext<PartnerStateModel>, action: UpdateAppConfig) {
    return this.partnerService.updateAppConfig(action.appConfig).pipe(
      tap((response: PartnerAppConfigResponse) => ctx.patchState({
        data: { ...ctx.getState().data, appConfig: response.data }
      })),
      this.catchResponse
    );
  }

  @Action(UpdateTextSections)
  updateTextSections(ctx: StateContext<PartnerStateModel>, action: UpdateTextSections): void {
    this.partnerService.updateTextSections(action.partnerId, action.textSections).subscribe(() => {
      ctx.patchState({
        data: { textSections: action.textSections, ...ctx.getState().data },
        loaded: true
      });
    });
  }

  @Action(UpdateSocialMedia)
  updateSocialMedia(ctx: StateContext<PartnerStateModel>, action: UpdateSocialMedia): void {
    this.partnerService.updateSocialMedia(action.partnerId, action.socialMedia).subscribe(() => {
      ctx.patchState({
        data: { socialMediaLinks: action.socialMedia, ...ctx.getState().data },
        loaded: true
      });
    });
  }

  @Action(UpdateContactInfo)
  updateContactInfo(ctx: StateContext<PartnerStateModel>, action: UpdateContactInfo) {
    return this.partnerService.updateContactInfo(action.contactInfo).pipe(
      map((response: PartnerContactInfoResponse) => response.data),
      tap((response: PartnerContactInfoDataResponse) => ctx.patchState({ data: {
        ...ctx.getState().data,
        contactInfo: {
          email: response.email.email,
          phoneNumber: response.phoneNumber
        }
      }})),
      this.catchResponse
    );
  }

  @Action(UpdatePartnerSettings)
  updatePartnerSettings(ctx: StateContext<PartnerStateModel>, action: UpdatePartnerSettings) {
    return this.partnerService.updatePartnerSettings(action.partnerId, action.settings).pipe(
      map((response: PartnerSettingsResponse) => response.data),
      tap((response: PartnerSettingsDataResponse) => ctx.patchState({ data: {
        ...ctx.getState().data,
        timezone: response.timezone,
        currency: response.currency,
        countryCode: response.countryCode
      }})),
      this.catchResponse
    );
  }

}
